import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchUsers } from '@/lib/api/users';
import { useDepartments } from '@/entities/department';

import type { RoleDepartment } from './RRolePicker';

/**
 * Отделы со списком сотрудников для «Добавить всех».
 * Имена берём из /departments, состав — из `departmentIds` пользователей
 * (тот же запрос `all-users`, что и у формы, поэтому лишней сетевой работы нет).
 */
export function useRoleDepartments(): RoleDepartment[] {
  const { data: usersData } = useQuery({
    queryKey: ['all-users'],
    queryFn: () => fetchUsers(''),
    staleTime: 5 * 60 * 1000,
  });
  const { data: deptData } = useDepartments();

  return useMemo(() => {
    // Ключ — строка: ID отдела числовой на legacy и UUID на v2.
    const membersByDept = new Map<string, number[]>();
    for (const u of usersData?.result ?? []) {
      if (String(u.Active ?? 'Y') !== 'Y') {
        continue;
      }
      const ids =
        u.departmentIds && u.departmentIds.length > 0
          ? u.departmentIds
          : u.DepartmentID
            ? [u.DepartmentID]
            : [];
      for (const d of ids) {
        const arr = membersByDept.get(String(d)) ?? [];
        arr.push(u.ID);
        membersByDept.set(String(d), arr);
      }
    }

    return (deptData?.result ?? [])
      .map((d) => ({
        id: d.id,
        name: String(d.name ?? '').trim(),
        memberIds: membersByDept.get(String(d.id)) ?? [],
      }))
      .filter((d) => d.name && d.memberIds.length > 0);
  }, [usersData, deptData]);
}
